import { useCallback, useEffect, useRef, useState } from 'react';
import type { Owner } from '@ff/core';
import { fetchSeason, type StoredSeason } from './api.ts';

interface SeasonState {
  data: StoredSeason | null;
  ownerName: Map<string, string>;
  ownerById: Map<string, Owner>;
  loading: boolean;
  error: string | null;
  /** True while a week is in progress (drives the LIVE badge). */
  live: boolean;
  /**
   * Re-fetch now, without waiting for the poll. Resolves true when the bundle
   * was actually replaced — callers that changed server state need to know the
   * difference between "refreshed" and "quietly still stale".
   */
  refresh: () => Promise<boolean>;
}

/** How often to re-fetch while polling. The server refreshes ESPN on its own
 * cadence; this just picks up the newest snapshot. */
const LIVE_POLL_MS = 45_000;

/** Load one season's bundle and expose owner lookups every view needs. */
export function useSeason(season: number): SeasonState {
  const [data, setData] = useState<StoredSeason | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * The effect's own loader, borrowed by refresh().
   *
   * A manual refresh must not be a second, parallel implementation of loading:
   * it has to honour the same mounted guard (so a response for last season can't
   * land on top of this one) and feed the same polling decision (so a sync that
   * discovers live games starts the poll instead of showing a frozen LIVE
   * badge). Going through this ref is what keeps those two in one place.
   */
  const loadRef = useRef<() => Promise<boolean>>(async () => false);

  useEffect(() => {
    let mounted = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Whether to keep polling. Updated only on a successful fetch; a failed
    // background refresh retains the last-known value so one transient error
    // doesn't stop live updates for the tab.
    let keepPolling = false;

    const schedule = () => {
      // Clear first: a manual refresh also lands here, and without this it would
      // leave the effect's pending timer running alongside its own.
      if (timer) clearTimeout(timer);
      if (mounted && keepPolling) timer = setTimeout(() => void load(false), LIVE_POLL_MS);
    };

    const load = (initial: boolean): Promise<boolean> =>
      fetchSeason(season)
        .then((d) => {
          if (!mounted) return false;
          setData(d);
          setError(null);
          // Poll while a week is live OR while we're in a game window but
          // pre-kickoff (liveWeeks still empty). The server flags the window
          // for the current season; this catches the "opened it at 12:45"
          // visitor once the 1pm games start.
          keepPolling = d.inGameWindow === true || d.bundle.data.liveWeeks.length > 0;
          return true;
        })
        .catch((e: Error) => {
          if (mounted && initial) setError(e.message);
          // On a background failure, keep whatever polling state we last knew,
          // so a transient 500 or dropped request just retries next tick. The
          // false is what lets a caller tell a silent failure from a refresh.
          return false;
        })
        .then((ok) => {
          if (mounted && initial) setLoading(false);
          schedule();
          return ok;
        });

    loadRef.current = () => load(false);
    setLoading(true);
    void load(true);
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
      loadRef.current = async () => false;
    };
  }, [season]);

  // Never sets `loading`: this runs after an action the user took, and blanking
  // the page they're looking at would read as a bug.
  const refresh = useCallback(() => loadRef.current(), []);

  const owners = data?.bundle.data.owners ?? [];
  return {
    data,
    loading,
    error,
    refresh,
    live: (data?.bundle.data.liveWeeks.length ?? 0) > 0,
    ownerName: new Map(owners.map((o) => [o.id, o.name])),
    ownerById: new Map(owners.map((o) => [o.id, o])),
  };
}
